import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, Save, Receipt, CalendarClock, RefreshCw, Layers, Plus, Trash2 } from 'lucide-react';
import { money } from '../lib/share';

const STATE_BADGE = {
  al_dia: { c: 'bg-green-100 text-green-700', t: 'Al día' },
  por_vencer: { c: 'bg-amber-100 text-amber-700', t: 'Por vencer' },
  vencido: { c: 'bg-red-100 text-red-700', t: 'Vencido' },
  sin_fecha: { c: 'bg-gray-100 text-gray-500', t: 'Sin fecha de corte' },
};

const PlanRow = ({ t, templates, onReload }) => {
  const [p, setP] = useState({ price: t.price ?? '', billing_cycle: t.billing_cycle || 'mensual', renew_date: t.renew_date || '' });
  const [busy, setBusy] = useState(false);
  const badge = STATE_BADGE[t.payment_state] || STATE_BADGE.sin_fecha;

  const applyModel = (id) => {
    const m = templates.find((x) => x.id === id);
    if (m) setP({ ...p, price: m.price, billing_cycle: m.billing_cycle });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.updateTenantTerms(t.tenant_id, {
        price: p.price === '' ? null : parseFloat(p.price),
        billing_cycle: p.billing_cycle,
        renew_date: p.renew_date || null,
      });
      toast.success(`SalesPRO: Plan de ${t.name} guardado (solo esta empresa)`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  const genInvoice = async () => {
    setBusy(true);
    try {
      const period = new Date().toLocaleDateString('es-VE', { month: 'long', year: 'numeric' });
      await api.createVendorInvoice({ kind: 'factura', auto: true, period, tenant_id: t.tenant_id, lines: [] });
      toast.success(`SalesPRO: Factura generada para ${t.name}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="border rounded-xl p-4 space-y-3" data-testid={`tenant-plan-${t.tenant_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-[#0A192F]" />
          <span className="font-semibold text-[#022C22]">{t.name}</span>
          <span className="text-[11px] text-[#64748B]">· {t.branches} sucursal(es) · comparten esta fecha</span>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${badge.c}`} data-testid={`tenant-plan-state-${t.tenant_id}`}>
          {badge.t}{typeof t.days_to_cutoff === 'number' ? ` · ${t.days_to_cutoff}d` : ''}
        </span>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Precio del plan ($)</Label>
          <Input type="number" value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} placeholder="Ej: 49" data-testid={`tenant-price-${t.tenant_id}`} />
        </div>
        <div>
          <Label className="text-xs">Ciclo</Label>
          <select value={p.billing_cycle} onChange={(e) => setP({ ...p, billing_cycle: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm">
            <option value="mensual">Mensual</option>
            <option value="trimestral">Trimestral</option>
            <option value="anual">Anual</option>
          </select>
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><CalendarClock size={12} /> Fecha de corte</Label>
          <Input type="date" value={p.renew_date || ''} onChange={(e) => setP({ ...p, renew_date: e.target.value })} data-testid={`tenant-cutoff-${t.tenant_id}`} />
        </div>
        <div>
          <Label className="text-xs">Aplicar modelo</Label>
          <select onChange={(e) => applyModel(e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm" data-testid={`tenant-apply-model-${t.tenant_id}`} defaultValue="">
            <option value="" disabled>Elegir…</option>
            {templates.map((m) => <option key={m.id} value={m.id}>{m.name} · {money(m.price)}/{m.billing_cycle}</option>)}
          </select>
        </div>
      </div>
      {t.last_invoice && (
        <p className="text-[11px] text-[#64748B]">Última factura: <b>{t.last_invoice.number}</b> · {money(t.last_invoice.total)} · {t.last_invoice.status}</p>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy} data-testid={`tenant-save-${t.tenant_id}`}><Save size={13} className="mr-1" /> Guardar plan</Button>
        <Button size="sm" variant="outline" onClick={genInvoice} disabled={busy} data-testid={`tenant-invoice-${t.tenant_id}`}><Receipt size={13} className="mr-1" /> Generar factura</Button>
      </div>
    </div>
  );
};

const PlanTemplates = ({ templates, setTemplates }) => {
  const [busy, setBusy] = useState(false);
  const add = () => setTemplates([...templates, { id: `new-${Date.now()}`, name: '', price: 0, billing_cycle: 'mensual', grace_days: 3 }]);
  const upd = (i, k, v) => { const a = [...templates]; a[i] = { ...a[i], [k]: v }; setTemplates(a); };
  const save = async () => {
    setBusy(true);
    try { const r = await api.savePlanTemplates(templates.map((t) => ({ ...t, price: parseFloat(t.price) || 0, grace_days: parseInt(t.grace_days) || 0 }))); setTemplates(r.data); toast.success('SalesPRO: Modelos de planes guardados'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };
  return (
    <div className="border rounded-xl p-4 bg-[#F8FAFC] space-y-3" data-testid="plan-templates">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-[#022C22] flex items-center gap-2 text-sm"><Layers size={15} /> Modelos de planes prediseñados</h4>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={add} data-testid="add-plan-template"><Plus size={13} /> Nuevo modelo</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="save-plan-templates"><Save size={13} className="mr-1" /> Guardar modelos</Button>
        </div>
      </div>
      <p className="text-[11px] text-[#64748B]">Crea planes reutilizables (ej. Básico, Pro, Premium) y aplícalos a cualquier empresa con un clic.</p>
      {templates.length === 0 && <p className="text-xs text-[#94A3B8]">Sin modelos aún. Crea el primero.</p>}
      {templates.map((m, i) => (
        <div key={m.id} className="grid grid-cols-[1.4fr_1fr_1fr_0.8fr_40px] gap-2 items-end" data-testid={`plan-template-${i}`}>
          <div><Label className="text-[11px]">Nombre</Label><Input value={m.name} onChange={(e) => upd(i, 'name', e.target.value)} placeholder="Ej: Pro" /></div>
          <div><Label className="text-[11px]">Precio ($)</Label><Input type="number" value={m.price} onChange={(e) => upd(i, 'price', e.target.value)} /></div>
          <div><Label className="text-[11px]">Ciclo</Label>
            <select value={m.billing_cycle} onChange={(e) => upd(i, 'billing_cycle', e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm">
              <option value="mensual">Mensual</option><option value="trimestral">Trimestral</option><option value="anual">Anual</option>
            </select>
          </div>
          <div><Label className="text-[11px]">Gracia (días)</Label><Input type="number" value={m.grace_days} onChange={(e) => upd(i, 'grace_days', e.target.value)} /></div>
          <Button size="icon" variant="ghost" onClick={() => setTemplates(templates.filter((_, j) => j !== i))}><Trash2 size={14} className="text-red-500" /></Button>
        </div>
      ))}
    </div>
  );
};

export const TenantPlans = () => {
  const [tenants, setTenants] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [r, tpl] = await Promise.all([api.getTenantsBilling(), api.getPlanTemplates()]);
      setTenants(r.data || []);
      setTemplates(tpl.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <Card data-testid="tenant-plans"><CardContent className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-[#022C22]">Planes por empresa</h3>
          <p className="text-xs text-[#64748B]">Cada empresa tiene su propio precio, ciclo y fecha de corte. Cambiar una NO afecta a las demás. Las sucursales comparten la fecha de su empresa madre.</p>
        </div>
        <Button size="sm" variant="ghost" onClick={load}><RefreshCw size={14} /></Button>
      </div>
      <PlanTemplates templates={templates} setTemplates={setTemplates} />
      {loading ? <p className="text-xs text-[#94A3B8]">Cargando…</p> :
        tenants.length === 0 ? <p className="text-xs text-[#94A3B8]">Aún no hay empresas con licencia.</p> :
        <div className="space-y-3">{tenants.map((t) => <PlanRow key={t.tenant_id} t={t} templates={templates} onReload={load} />)}</div>}
    </CardContent></Card>
  );
};

export default TenantPlans;
